/**
 * MfeGtmService
 *
 * Centralised GTM manager for the MFE host shell.
 *
 * Responsibilities:
 *  1. Listen for 'gtm:init' events from remote MFEs (via MFEEventBus)
 *  2. Inject a GTM <script> with a namespaced dataLayer per app
 *  3. Track which containers are already loaded — prevent duplicate injection
 *  4. Emit 'gtm:ready' so the remote knows GTM is active
 *  5. Push synthetic 'mfe_page_view' events on every MFE app switch
 *
 * Naming convention:
 *   appId 'cap-loyalty-ui' → dataLayer variable 'dataLayer_cap_loyalty_ui'
 *
 * Usage:
 *   import MfeGtmService from './services/MfeGtmService';
 *   MfeGtmService.init();                                      // call once in app.js
 *   MfeGtmService.pushPageView('cap-loyalty-ui', '/path', ''); // call on MFE mount
 */

import { MFEEventBus } from '@capillarytech/cap-ui-utils';

// Registry: { [appId]: { containerId, dataLayerName } }
const loadedContainers = {};

/**
 * Converts an appId to a safe dataLayer variable name.
 * 'cap-loyalty-ui' → 'dataLayer_cap_loyalty_ui'
 */
const getDataLayerName = appId =>
  `dataLayer_${appId.replace(/-/g, '_')}`;

/**
 * Injects a GTM script tag with a namespaced dataLayer.
 * Mirrors the standard GTM snippet — the &l= param tells gtm.js
 * which window variable to listen to.
 */
const injectGtmScript = (containerId, dataLayerName) => {
  // Create the namespaced array before the script loads so queued
  // events are not lost (GTM drains the array once gtm.js initialises).
  window[dataLayerName] = window[dataLayerName] || [];
  window[dataLayerName].push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const firstScript = document.getElementsByTagName('script')[0];
  const script = document.createElement('script');
  script.async = true;
  // &l= tells gtm.js which window variable to use as the dataLayer
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}&l=${dataLayerName}`;
  firstScript.parentNode.insertBefore(script, firstScript);
};

/**
 * Initialise the GTM service. Call once on host shell boot (app.js).
 * Sets up the listener for 'gtm:init' events from remote MFEs.
 */
const init = () => {
  MFEEventBus.on('gtm:init', ({ appId, containerId }) => {
    if (!appId || !containerId) {
      console.warn('[MfeGtmService] gtm:init received without appId or containerId', { appId, containerId });
      return;
    }

    const dataLayerName = getDataLayerName(appId);

    // Already loaded — just confirm ready, no re-injection
    if (loadedContainers[appId]) {
      MFEEventBus.emit('gtm:ready', {
        appId,
        dataLayerName: loadedContainers[appId].dataLayerName,
      });
      return;
    }

    // First load — inject GTM script with namespaced dataLayer
    injectGtmScript(containerId, dataLayerName);
    loadedContainers[appId] = { containerId, dataLayerName };

    MFEEventBus.emit('gtm:ready', { appId, dataLayerName });
    console.log(`[MfeGtmService] GTM initialised for ${appId} → ${dataLayerName}`);
  });
};

/**
 * Push a synthetic page view event when an MFE app mounts or the user
 * navigates between MFE apps (no full page reload occurs in MFE mode).
 *
 * Pushes to:
 *  - the app's own namespaced dataLayer (for the app's GTM container)
 *  - dataLayer_host (for cross-app page view tracking in the host container)
 *
 * @param {string} appId     - e.g. 'cap-loyalty-ui'
 * @param {string} pagePath  - e.g. '/loyalty/ui/programs'
 * @param {string} pageTitle - e.g. 'Loyalty Programs'
 */
const pushPageView = (appId, pagePath, pageTitle) => {
  const container = loadedContainers[appId];
  if (container && window[container.dataLayerName]) {
    window[container.dataLayerName].push({
      event: 'mfe_page_view',
      page_path: pagePath,
      page_title: pageTitle,
      app_id: appId,
    });
  }

  // Always push to host dataLayer for cross-app tracking
  if (window.dataLayer_host) {
    window.dataLayer_host.push({
      event: 'mfe_page_view',
      page_path: pagePath,
      page_title: pageTitle,
      app_id: appId,
    });
  }
};

/**
 * Returns a snapshot of the loaded container registry.
 * Useful for debugging in dev tools.
 */
const getLoadedContainers = () => ({ ...loadedContainers });

export default {
  init,
  pushPageView,
  getLoadedContainers,
};
